import React from 'react';
import { Grid, FormControlLabel, Checkbox, Typography, Button } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import TimePicker from 'components/TimePicker';
import DatePicker from 'components/DatePicker';
import moment from 'moment-timezone';
import Delete from '@mui/icons-material/Delete';
import classNames from 'classnames';
import { ExceptionDateObject } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	value: ExceptionDateObject;
	onChange: (value: ExceptionDateObject) => void;
	onDelete: () => void;
	reservedDates?: string[];
}

const OpeningHoursExceptionRow = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { onChange, onDelete } = props;
	const { openTime, closeTime, isClosed, date } = props.value;
	const mobileCalendar = window.matchMedia(
		'screen and (orientation:portrait) and (max-width: 640px)',
	).matches;

	const handleDateChange = (selectedDate: moment.Moment | null) => {
		if (!selectedDate) {
			return;
		}
		onChange({
			...props.value,
			date: selectedDate.format('YYYY-MM-DD'),
		});
	};

	const handleOpenTimeChange = (value: string) => {
		onChange({
			...props.value,
			openTime: value,
		});
	};

	const handleCloseTimeChange = (value: string) => {
		onChange({
			...props.value,
			closeTime: value,
		});
	};

	const handleToggleIsClosed = () => {
		onChange({
			...props.value,
			isClosed: !isClosed,
		});
	};

	const isDayOutsideRange = (day: moment.Moment) => {
		return moment(day).isBefore();
	};

	const isDayReserved = (day: moment.Moment) => {
		const dateString = moment(day).format('YYYY-MM-DD');

		if (dateString !== props.value.date && props.reservedDates?.includes(dateString)) {
			return true;
		}

		return false;
	};

	return (
		<Grid container alignItems="flex-end" spacing={1}>
			<Grid item xs={10} sm={3} className={classes.smallTopOrder}>
				<DatePicker
					value={date}
					onChange={handleDateChange}
					id="date"
					styleProps={{
						height: 35,
						bordered: true,
					}}
					singleDatePickerProps={{
						showDefaultInputIcon: !mobileCalendar,
						isOutsideRange: isDayOutsideRange,
						isDayBlocked: isDayReserved,
					}}
				/>
			</Grid>
			<Grid item xs={4} sm={2}>
				<Typography variant="body2">{t('common:times.opens')}</Typography>
				<TimePicker
					onChange={handleOpenTimeChange}
					selectedDate={moment(openTime, 'HH:mm').toISOString()}
					noMinDate={true}
					outlined={true}
					disabled={isClosed}
				/>
			</Grid>
			<Grid item xs={4} sm={2}>
				<Typography variant="body2">{t('common:times.closes')}</Typography>
				<TimePicker
					onChange={handleCloseTimeChange}
					selectedDate={moment(closeTime, 'HH:mm').toISOString()}
					noMinDate={true}
					outlined={true}
					disabled={isClosed}
				/>
			</Grid>
			<Grid item xs={4} sm={3} container justifyContent="flex-end">
				<FormControlLabel
					control={<Checkbox color="primary" checked={isClosed} onChange={handleToggleIsClosed} />}
					label={t('common:times.closed')}
				/>
			</Grid>
			<Grid item xs={2} sm={2} className={classes.smallTopOrder}>
				<Button onClick={onDelete}>
					<Delete className={classNames(classes.removeBtn, classes.icon)} />
				</Button>
			</Grid>
		</Grid>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		smallTopOrder: {
			[theme.breakpoints.down('sm')]: {
				order: -1,
			},
		},
		removeBtn: {
			display: 'block',
			padding: 8,
		},
		icon: {
			width: 40,
			height: 40,
			color: theme.palette.common.black,
		},
		alignRight: {
			textAlign: 'right',
		},
	}),
);

export default OpeningHoursExceptionRow;
